/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ButtonProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledClickable } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xBaseTheme } from "@rc/XDL/types";
import { css } from "styled-components";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

export const buttonSizeVariants = {
    small: {
        height: 40,
        fontSize: 16,
        weight: 500,
        letterSpacing: 0,
        lineHeight: 20,
        padding: "6px 24px",
        icon: 24,
        iconPadding: 0,
        iconHoverMargin: 4,
    },
    medium: {
        height: 47,
        fontSize: 16,
        weight: 500,
        letterSpacing: 0,
        lineHeight: 20,
        padding: "10px 33px",
        icon: 26,
        iconPadding: 0,
        iconHoverMargin: 12,
    },
    large: {
        height: 47,
        fontSize: 16,
        weight: 500,
        letterSpacing: 0,
        lineHeight: 21,
        padding: "10px 33px",
        icon: 26,
        iconPadding: 0,
        iconHoverMargin: 12,
    },
};

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

const buttonPresentationStyle = (buttonType: string) => {
    return {
        ...ButtonPresentationPropsDefault,
        buttonType: "solid",
        shape: "rounded",
        sizeVariant: "medium",
        margeCss: true,
        css: css`
            &:disabled {
                opacity: 0.33;
                box-shadow: none;
                border-color: ${getColor("primary.main")};
            }
            &,
            &:disabled,
            &:focus,
            &:active {
                & {
                    background: ${buttonType === "primary" ? getColor("primary.main") : getColor("common.ui1")};
                    color: ${buttonType === "primary" ? getColor("common.ui1") : getColor("primary.main")};

                    svg {
                        color: ${buttonType === "primary" ? getColor("common.ui1") : getColor("primary.main")};
                    }
                }
            }
            &:hover:not(:disabled) {
                [class*="IconWrapper"] {
                    right: ${({ _sizeVariant }) => (_sizeVariant === "small" ? "4px" : "13px")};
                }
                [class*="TypographyStyle"]:not(.fixed) {
                    position: relative;
                    left: -10px;
                }
            }
            position: relative;
            overflow: hidden;
            text-align: center;
            outline: none !important;
            height: auto;
            min-height: ${(props: BWT) =>
                props["_size"] || get(buttonSizeVariants, [props["_sizeVariant"], "height"])}px;
            padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};

            text-shadow: none;
            letter-spacing: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "letterSpacing"])}px;
            border: 1px solid ${getColor("primary")};
            border-radius: 24px;
            &,
            * {
                font-size: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "fontSize"])}px;
                font-weight: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "weight"])};
                line-height: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "lineHeight"])}px;
                text-transform: capitalize;
            }

            [class*="IconWrapper"] {
                position: absolute;
                right: -32px;
                top: 50%;
                transform: translateY(-50%);
                transition: all 0.2s ease;
                height: 32px;
                overflow: hidden;
                transform-origin: 50% 50%;
                &,
                svg {
                    width: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "icon"])}px;
                    height: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "icon"])}px;
                }
            }

            ${TypographyStyle} {
                position: relative;
                left: 0;
                transition: all 0.2s ease;
            }

            ${StyledClickable} {
                z-index: 2;
            }
        `,
    };
};

const buttonVariants = {
    primary: {
        ...buttonPresentationStyle("primary"),
        css: [
            buttonPresentationStyle("primary").css,
            css`
                &:hover:not(:disabled) {
                    background: ${getColor("primary.main")};
                    border-color: ${getColor("primary.main")};
                }
                &:focus:not(:disabled),
                &:focus-visible:not(:disabled),
                &:active:not(:disabled) {
                    background: ${getColor("common.ui6")};
                    border-color: ${getColor("common.ui6")};
                    color: ${getColor("common.ui1")};
                    outline: none;
                }
                &:hover svg {
                    color: ${getColor("common.ui1")};
                    background: transparent;
                }

                svg {
                    color: ${getColor("common.ui1")};
                }
            `,
        ],
    },
    secondary: {
        ...buttonPresentationStyle("secondary"),
        css: [
            buttonPresentationStyle("secondary").css,
            css`
                &:focus:not(:disabled),
                &:focus-visible:not(:disabled),
                &:active:not(:disabled) {
                    border-color: ${getColor("primary")};
                }
                &:hover:not(:disabled) {
                    background: ${getColor("common.ui1")};
                    border-color: ${getColor("primary")};
                }
                &:hover svg {
                    background: transparent;
                }
            `,
        ],
    },
    tertiary: {
        ...buttonPresentationStyle("secondary"),
        css: [
            buttonPresentationStyle("secondary").css,
            css`
                &:focus:not(:disabled),
                &:focus-visible:not(:disabled),
                &:active:not(:disabled) {
                    border-color: ${getColor("primary")};
                }
                &:hover:not(:disabled) {
                    background: ${getColor("common.ui1")};
                    border-color: ${getColor("primary")};
                }
                &:hover svg {
                    background: transparent;
                }
            `,
        ],
    },
} as xBaseTheme["button"];

export default buttonVariants;
